import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { loadFontStyle, saveFontStyle } from "@/lib/styleService";
import Logger from "@/utility/logger";
import { FontStyleSettings } from "@/lib/types";
import { defaultDraftStyles, defaultConfirmedStyles, defaultUserStyles } from "@/lib/defaultFontStyles";

export interface StyleContextData {
  draftStyles: FontStyleSettings;
  confirmedStyles: FontStyleSettings;
  userStyles: FontStyleSettings;
  userStylesEnabled: boolean;
  setDraftStyles: (styles: FontStyleSettings) => Promise<void>;
  setConfirmedStyles: (styles: FontStyleSettings) => Promise<void>;
  setUserStyles: (styles: FontStyleSettings) => Promise<void>;
  setUserStylesEnabled: (enabled: boolean) => void;
}

export const StyleContext = createContext<StyleContextData>({} as StyleContextData);

export const StyleProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [draftStyles, setDraft] = useState<FontStyleSettings>(defaultDraftStyles);
  const [confirmedStyles, setConfirmed] = useState<FontStyleSettings>(defaultConfirmedStyles);
  const [userStyles, setUser] = useState<FontStyleSettings>(defaultUserStyles);
  const [userStylesEnabled, setUserStylesEnabled] = useState<boolean>(false);

  // Initialize by loading current settings from document
  useEffect(() => {
    async function loadAll() {
      const ds = await loadFontStyle("draftStyles");
      setDraft(ds);
      const cs = await loadFontStyle("confirmedStyles");
      setConfirmed(cs);
      const us = await loadFontStyle("userStyles");
      setUser(us);
    }
    loadAll().catch((e) => Logger.error("Failed to load font styles", e));
  }, []);

  const setDraftStyles = async (styles: FontStyleSettings) => {
    await saveFontStyle("draftStyles", styles);
    setDraft(styles);
  };

  const setConfirmedStyles = async (styles: FontStyleSettings) => {
    await saveFontStyle("confirmedStyles", styles);
    setConfirmed(styles);
  };

  const setUserStyles = async (styles: FontStyleSettings) => {
    await saveFontStyle("userStyles", styles);
    setUser(styles);
  };

  return (
    <StyleContext.Provider
      value={{
        draftStyles,
        confirmedStyles,
        userStyles,
        userStylesEnabled,
        setDraftStyles,
        setConfirmedStyles,
        setUserStyles,
        setUserStylesEnabled,
      }}
    >
      {children}
    </StyleContext.Provider>
  );
};

export const useFontStyle = () => {
  const ctx = useContext(StyleContext);
  if (!ctx) throw new Error("useFontStyle must be used within a StyleProvider");
  return ctx;
};
